import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, FilePlus2, Camera, UserPlus, UserMinus, Users, Receipt, GitFork, ClipboardCheck, Zap, FolderPlus, RefreshCw, Pencil, Building2 } from "lucide-react";
import { useTrace } from "@/shell/TraceProvider";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { InviteModal } from "@/components/InviteModal";
import { AddMembersModal } from "@/components/AddMembersModal";
import { AssignLeadContractorModal } from "@/components/AssignLeadContractorModal";
import { RemoveMemberModal } from "@/components/RemoveMemberModal";
import { CreateProjectModal } from "@/components/CreateProjectModal";
import { ChangeProjectStatusModal } from "@/components/ChangeProjectStatusModal";
import { EditProjectModal } from "@/components/EditProjectModal";
import { RequestProjectEditModal } from "@/components/RequestProjectEditModal";
import { PendingEditRequestPanel } from "@/components/PendingEditRequestPanel";
import { ProjectStatusBanner } from "@/components/ProjectStatusBanner";
import { ProjectGovernanceHistory } from "@/components/ProjectGovernanceHistory";
import { demoModeActive } from "@/domain/api";
import { railSeverityOfAlerts } from "@/screens/shared/briefing";
import {
  Stat,
  SpendBudgetStat,
  ProgressBar,
  MilestoneStrip,
  Tabs,
  Pill,
  EntityStatusPill,
  Button,
  DataTable,
  type Column,
  RightRail,
  RailSection,
  ActivityFeed,
  type ActivityFeedItem,
  eventToActivityItem,
  Briefing,
  type BriefingAlert,
  AttentionBanner,
  AttentionAction,
  Avatar,
  EmptyState,
} from "@/design/components";
import { formatRelativeShort } from "@/design/format";
import { roleLabelFor } from "@/domain/cascadeRoles";
import {
  useSeed,
  useProject,
  useSubProjects,
  useMilestones,
  useOrgIndex,
  usePeopleIndex,
  useTerminology,
  useIsSubContractor,
  useVisibleSubProjects,
  useMoney,
  useMoneyParts,
  useActiveProfile,
  useActorId,
  useProjectMembers,
  useProjectOrganisations,
  useActiveProgrammeId,
  type TeamMember,
  useAuditEvents,
} from "@/domain/hooks";
import { useStartSiteVisit } from "@/domain/useMutations";
import type { SubProject } from "@/domain/types";
import { ApprovalQueue } from "@/screens/approval/ApprovalQueue";
import { AnalyticsSurface } from "@/screens/analytics/AnalyticsSurface";
import { AuditTrail } from "@/screens/audit/AuditTrail";
import { EvidenceByProject } from "@/screens/programme/EvidenceByProject";
import { DeliveryProgress } from "@/screens/programme/DeliveryProgress";
import { RecordSpendDrawer } from "@/screens/approval/RecordSpendDrawer";

export function ProjectManagementView() {
  const { workspaceId, projectId = "" } = useParams();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addSubProjectOpen, setAddSubProjectOpen] = useState(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState(false);
  const [editProjectOpen, setEditProjectOpen] = useState(false);
  const [requestEditOpen, setRequestEditOpen] = useState(false);
  const seed = useSeed();
  const programmeId = useActiveProgrammeId(workspaceId);
  const project = useProject(projectId, workspaceId);
  const allSubs = useSubProjects(projectId, workspaceId);
  const milestones = useMilestones(projectId, workspaceId);
  const orgs = useOrgIndex(workspaceId);
  const people = usePeopleIndex(workspaceId);
  const term = useTerminology();
  const money = useMoney();
  const mp = useMoneyParts();
  const { openTrace } = useTrace();
  const navigate = useNavigate();
  const profile = useActiveProfile();
  const actorId = useActorId();
  const { start: startSiteVisitHook, isPending: startingVisit } = useStartSiteVisit();
  const base = `/workspace/${workspaceId}`;

  async function startVisit() {
    const newId = await startSiteVisitHook({ projectId, visitorId: actorId });
    if (newId) navigate(`/stratus/site-visits/${newId}`);
  }
  const [tab, setTab] = useState("subprojects");
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Switch tab and bring the tabbed surface into view. The attention banner and the
  // right-rail briefing sit well above the tabs, so a bare setTab looks unresponsive
  // on a tall page — the activated tab is below the fold. Defer one frame so React
  // commits the tab change first, then jump (instant, not smooth: a smooth animation
  // gets cancelled by the re-render and only nudges a few px).
  function goToTab(next: string) {
    setTab(next);
    requestAnimationFrame(() => tabsRef.current?.scrollIntoView({ block: "start" }));
  }

  // Governance: a sub-contractor PM sees only their own work packages and budget on this
  // project, never the whole-project figures or other trades.
  const isSubContractor = useIsSubContractor();
  const myVisibleSubs = useVisibleSubProjects(workspaceId);
  const mySubsHere = myVisibleSubs.filter((s) => s.projectId === projectId);
  const subs = isSubContractor ? mySubsHere : allSubs;
  const myScopeBudget = mySubsHere.reduce((a, s) => a + s.budgeted, 0);
  const myScopeSpent = mySubsHere.reduce((a, s) => a + s.spent, 0);
  const myScopeEvidenced = mySubsHere.reduce((a, s) => a + s.evidenced, 0);
  const approvalScope = isSubContractor && mySubsHere.length === 1 ? { subProjectId: mySubsHere[0].id } : { projectId };
  // Recent activity — structured feed from this project's audit events.
  const projectEvents = useAuditEvents({ projectId });

  if (!project) {
    return (
      <WorkspaceShell>
        <div className="p-8">
          <EmptyState title="Project not found" body="This project is not in the active workspace seed." />
        </div>
      </WorkspaceShell>
    );
  }

  // ZEUS-132 / ZEUS-139 — UI convenience only, server enforces stratus_admin
  // or funder_approver (assertCanCreateProjectDirectly, zeusProject.js).
  // profile.group directly, not useCanManageProjectGovernance() (demo-only,
  // resolves to null — and therefore "show" under the old fallback — for
  // every real logged-in user regardless of role; see WorkspacePages.tsx's
  // showCreate for the full explanation).
  const showAddSubProject = profile.group === "oversight" || profile.group === "funder";

  const lead = orgs[project.leadContractorId];
  const mySubIdsHere = new Set(mySubsHere.map((s) => s.id));
  const pendingReqs = seed.spendRequests.filter((r) => {
    if (r.state !== "proposed" && r.state !== "flagged") return false;
    if (isSubContractor) return r.subProjectId ? mySubIdsHere.has(r.subProjectId) : false;
    return r.projectId === projectId;
  });
  const pending = pendingReqs.length;
  const oldestPending = pendingReqs.reduce<string>((oldest, r) => (!oldest || r.submittedAt < oldest ? r.submittedAt : oldest), "");

  // Right-rail briefing — itemised alerts derived from real signals on this project.
  const scopeSpent = isSubContractor ? myScopeSpent : project.spent;
  const scopeEvidenced = isSubContractor ? myScopeEvidenced : project.evidenced;
  const unevidenced = Math.max(0, scopeSpent - scopeEvidenced);
  const askIdsHere = new Set(seed.spendRequests.filter((r) => r.projectId === projectId).map((r) => r.id));
  const openFindings = isSubContractor
    ? []
    : seed.aiFindings.filter((f) => f.status === "open" && f.subjectAskId && askIdsHere.has(f.subjectAskId));
  const briefingAlerts: BriefingAlert[] = [];
  // TODO(heimdall): AI analyst not built yet — re-enable the Heimdall findings briefing alert.
  // if (openFindings.length > 0)
  //   briefingAlerts.push({
  //     id: "findings",
  //     severity: openFindings.some((f) => f.severity === "serious") ? "serious" : "attention",
  //     title: `Heimdall has ${openFindings.length} open finding${openFindings.length > 1 ? "s" : ""} here`,
  //     detail: "Each cites evidence and needs a person to decide.",
  //     action: { label: "Review findings", onClick: () => goToTab("analytics") },
  //   });
  if (pending > 0)
    briefingAlerts.push({
      id: "pending",
      severity: "attention",
      title: `${pending} spend ask${pending > 1 ? "s" : ""} awaiting approval`,
      detail: oldestPending ? `Oldest submitted ${formatRelativeShort(oldestPending)} ago.` : undefined,
      action: { label: "Open approvals", onClick: () => goToTab("approvals") },
    });
  if (unevidenced > 0)
    briefingAlerts.push({
      id: "evidence",
      severity: "info",
      title: `Evidence gap: ${money(unevidenced)} not yet evidenced`,
      detail: `${Math.round((scopeEvidenced / Math.max(1, scopeSpent)) * 100)}% of spend evidenced so far.`,
      action: { label: "View evidence", onClick: () => goToTab("evidence") },
    });

  const activityItems: ActivityFeedItem[] = projectEvents.slice(0, 6).map((e) =>
    eventToActivityItem(e, { meta: orgs[e.actorOrganisationId]?.name, amount: e.amount ? money(e.amount) : undefined }),
  );
  const lastActivityAt = projectEvents[0]?.timestamp;

  const subColumns: Column<SubProject>[] = [
    { key: "name", header: term.subProject, cell: (sp) => <span className="font-medium text-ink">{sp.name}</span> },
    { key: "contractor", header: "Contractor", cell: (sp) => <div className="flex items-center gap-1.5"><span className="text-mute">{orgs[sp.leadContractorId]?.name}</span><Pill variant="neutral">Sub</Pill></div> },
    { key: "budget", header: "Budgeted", align: "right", cell: (sp) => <span className="tabular-nums text-mute">{money(sp.budgeted)}</span> },
    { key: "spent", header: "Spent", cell: (sp) => <div className="w-28"><span className="whitespace-nowrap text-sm tabular-nums text-ink">{money(sp.spent)}</span><ProgressBar value={sp.budgeted ? sp.spent / sp.budgeted : 0} className="mt-1" /></div> },
    { key: "evidenced", header: "Evidenced", align: "right", cell: (sp) => <span className="tabular-nums text-mute">{money(sp.evidenced)}</span> },
    { key: "status", header: "Status", cell: (sp) => <EntityStatusPill status={sp.status} /> },
  ];

  return (
    <WorkspaceShell
      rightRail={
        <RightRail
          collapsed={railCollapsed}
          onToggle={() => setRailCollapsed((c) => !c)}
          summary={[{ id: "briefing", count: briefingAlerts.length, severity: railSeverityOfAlerts(briefingAlerts) }]}
        >
          <RailSection title="Quick actions" tone="action" icon={Zap}>
            <div className="flex flex-col gap-2">
              {/* Spend/evidence actions are contractor-only: oversight approves and observes, it
                  never raises asks, records spend, or submits evidence. Invite member stays
                  available to everyone (oversight manages onboarding). */}
              {profile.group === "contractor" && (
                <>
                  <Button variant="secondary" className="justify-start" onClick={() => navigate(`${base}/raise`)}><FilePlus2 className="h-4 w-4" /> Raise spend ask</Button>
                  <Button variant="secondary" className="justify-start" onClick={() => setRecordOpen(true)}><Receipt className="h-4 w-4" /> Record / import spend</Button>
                  <Button variant="secondary" className="justify-start" onClick={() => navigate(`${base}/submit-evidence`)}><Camera className="h-4 w-4" /> Submit evidence</Button>
                </>
              )}
              <Button variant="secondary" className="justify-start" onClick={() => setInviteOpen(true)}><UserPlus className="h-4 w-4" /> Invite member</Button>
            </div>
          </RailSection>
          {/* On this surface the Briefing IS the attention queue, so it reads as an action
              section with the open-alert count and the loudest alert's tint. */}
          <RailSection
            title="Briefing"
            tone="action"
            count={briefingAlerts.length}
            severity={railSeverityOfAlerts(briefingAlerts)}
          >
            <Briefing alerts={briefingAlerts} updatedAt={lastActivityAt} />
          </RailSection>
          <RailSection title="Activity" divider>
            <ActivityFeed density="compact" items={activityItems} onViewAll={() => goToTab("audit")} emptyLabel="No activity yet on this project." />
          </RailSection>
        </RightRail>
      }
    >
      <div className="px-4 py-5 md:px-8 md:py-6">
        {/* Header strip */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold tracking-tight text-ink">{project.name}</h1>
              <EntityStatusPill status={project.status} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-mute">
              <span className="flex items-center gap-1.5"><Avatar initials={people[lead?.primaryContactPersonId ?? ""]?.avatarInitials ?? "?"} size={20} /> {lead?.name}</span>
              <span className="text-faint">·</span>
              <span>{project.procurementLineRef}</span>
              <span className="text-faint">·</span>
              <span>{project.district}</span>
            </div>
          </div>
          {/* overflow-x-auto rather than flex-wrap: up to 5 actions plus the
              Dashboard/Presentation toggle don't fit one row on a phone
              viewport — this turns it into a horizontally-scrollable strip
              instead of clipping/squishing button labels, matching the
              overflow-x-auto idiom used for MilestoneStrip/Tabs. shrink-0 on
              every child keeps buttons at their natural width instead of
              being squeezed to fit. */}
          <div className="flex items-center gap-2 overflow-x-auto">
            {profile.group === "oversight" && (
              <>
                <Button variant="ghost" size="sm" className="shrink-0" disabled={startingVisit} onClick={startVisit}>
                  <ClipboardCheck className="h-4 w-4" /> Start site visit
                </Button>
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setChangeStatusOpen(true)}>
                  <RefreshCw className="h-4 w-4" /> Change status
                </Button>
                <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setEditProjectOpen(true)}>
                  <Pencil className="h-4 w-4" /> Edit project
                </Button>
              </>
            )}
            {profile.group === "contractor" && project.status === "published" && !demoModeActive() && (
              <Button variant="ghost" size="sm" className="shrink-0" onClick={() => setRequestEditOpen(true)}>
                <Pencil className="h-4 w-4" /> Request edit
              </Button>
            )}
            {/* In-app navigation, NOT target="_blank": the JWT lives only in
                this tab's memory (authStore.ts persists `user`, never the
                token), and the auth cookie is SameSite=Strict so it never
                reaches the cross-site API. A new tab therefore starts
                tokenless, every request goes out anonymous, the server returns
                an empty permission-filtered project list, and this page
                rendered "This public record is not available." every time.
                Opening it in a new tab is safe again once the cookie is
                SameSite=None and AuthGuard re-derives a missing token. */}
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0"
              onClick={() => navigate(`/public/${workspaceId}/${projectId}`)}
            >
              <ArrowUpRight className="h-4 w-4" /> Stakeholder view
            </Button>
            <Button variant="ghost" size="sm" className="shrink-0" aria-label="Trace source" onClick={() => openTrace({ kind: "project", id: projectId }, { from: "Project dashboard" })}>
              <GitFork className="h-4 w-4" /> Trace source
            </Button>
            <div className="inline-flex shrink-0 items-center rounded-md border border-line bg-card p-0.5">
              <span className="whitespace-nowrap rounded px-3 py-1 text-sm font-medium bg-subtle text-ink">Dashboard</span>
              <button onClick={() => navigate(`${base}/projects/${projectId}/summary`)} className="whitespace-nowrap rounded px-3 py-1 text-sm text-mute hover:text-ink">
                Presentation view
              </button>
            </div>
          </div>
        </div>

        {/* ZEUS-135 — persistent status banner, draft/review only. */}
        <ProjectStatusBanner status={project.status} />

        {/* Attention banner — the page's primary call to action, above the figures.
            It renders only when work is waiting; absence is the all-clear signal. */}
        {pending > 0 && (
          <AttentionBanner
            className="mt-5"
            title={`${pending} spend ask${pending > 1 ? "s" : ""} awaiting your approval`}
            meta={oldestPending ? `Oldest submitted ${formatRelativeShort(oldestPending)} ago` : undefined}
            action={
              <AttentionAction onClick={() => goToTab("approvals")}>
                Review {pending} spend ask{pending > 1 ? "s" : ""} <ArrowRight className="h-3.5 w-3.5" />
              </AttentionAction>
            }
          />
        )}

        {/* Figures + milestones. Sub-contractors get a milestone strip (they have no gate
            view); primes/oversight get the single milestone representation inside the gate
            panel below, so milestones are never drawn twice on one screen. */}
        {isSubContractor ? (
          <div className="mt-6 grid grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-5">
              <p className="mb-2 text-xs font-medium text-faint">Your scope on this project ({mySubsHere.map((s) => s.name).join(", ")})</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 flex flex-col rounded-card border border-line bg-card p-4">
                  <span className="text-meta text-faint">Your budget</span>
                  <span className="mt-2.5 flex items-baseline gap-1.5">
                    <span className="num whitespace-nowrap text-figure font-semibold text-ink">{mp(myScopeBudget).value}</span>
                    <span className="text-sm font-medium text-faint">{mp(myScopeBudget).unit}</span>
                  </span>
                  <span className="mt-2 text-xs text-faint">Approved for your packages</span>
                  <SpendBudgetStat
                    className="mt-3"
                    label="Spent of your budget"
                    spent={myScopeSpent}
                    budget={myScopeBudget}
                    spentLabel={money(myScopeSpent)}
                    budgetLabel={money(myScopeBudget)}
                  />
                </div>
                <Stat label="Evidenced" value={mp(myScopeEvidenced).value} unit={mp(myScopeEvidenced).unit} sub={`${Math.round((myScopeEvidenced / Math.max(1, myScopeSpent)) * 100)}% of your spend`} trend={<ProgressBar value={myScopeEvidenced / Math.max(1, myScopeSpent)} tone="ok" />} />
                <Stat label="Evidence coverage" value={`${Math.round((myScopeEvidenced / Math.max(1, myScopeSpent)) * 100)}%`} sub="of your spend" />
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <h2 className="mb-2 text-meta text-faint">Milestones</h2>
              <MilestoneStrip milestones={milestones} variant="compact" />
            </div>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="col-span-2 flex flex-col rounded-card border border-line bg-card p-4">
              <span className="text-meta text-faint">Budgeted</span>
              <span className="mt-2.5 flex items-baseline gap-1.5">
                <span className="num whitespace-nowrap text-figure font-semibold text-ink">{mp(project.budgeted).value}</span>
                <span className="text-sm font-medium text-faint">{mp(project.budgeted).unit}</span>
              </span>
              <span className="mt-2 text-xs text-faint">Total approved budget</span>
              <SpendBudgetStat
                className="mt-3"
                label="Spent of budget"
                spent={project.spent}
                budget={project.budgeted}
                spentLabel={money(project.spent)}
                budgetLabel={money(project.budgeted)}
              />
            </div>
            <Stat label="Evidenced" value={mp(project.evidenced).value} unit={mp(project.evidenced).unit} sub={`${Math.round((project.evidenced / Math.max(1, project.spent)) * 100)}% of spend`} trend={<ProgressBar value={project.evidenced / Math.max(1, project.spent)} tone="ok" />} />
            <Stat label="Contingency" value={mp(project.redundancy).value} unit={mp(project.redundancy).unit} sub={`${Math.round((project.redundancy / Math.max(1, project.budgeted)) * 100)}% of budget held`} />
          </div>
        )}

        {/* Delivery and disbursement — the single milestone representation for primes/oversight */}
        {!isSubContractor && (
          <div className="mt-6">
            <DeliveryProgress projectId={projectId} approvalsHref={`${base}/approvals`} showHeaderLink={false} showProjectName={false} />
          </div>
        )}

        {/* Tabs */}
        <div ref={tabsRef} className="mt-8 scroll-mt-4">
          <Tabs
            items={[
              { value: "subprojects", label: term.subProject + "s" },
              { value: "approvals", label: "Approvals" },
              { value: "evidence", label: "Evidence" },
              { value: "audit", label: "Audit trail" },
              { value: "team", label: "Participants" },
              { value: "analytics", label: "Analytics" },
            ]}
            value={tab}
            onValueChange={setTab}
          />
        </div>

        <div className="mt-6">
          {tab === "subprojects" && (
            <div className="space-y-3">
              {showAddSubProject && (
                <div className="flex justify-end">
                  <Button size="sm" onClick={() => setAddSubProjectOpen(true)}>
                    <FolderPlus className="h-4 w-4" /> Add sub-project
                  </Button>
                </div>
              )}
              <DataTable columns={subColumns} rows={subs} rowKey={(sp) => sp.id} onRowClick={(sp) => navigate(`${base}/projects/${projectId}/sub/${sp.id}`)} />
            </div>
          )}
          {tab === "approvals" && <ApprovalQueue audience={isSubContractor ? "Sub-contractor lead" : "Prime contractor lead"} scope={approvalScope} />}
          {tab === "evidence" && (
            <div className="space-y-3">
              <div className="rounded-md border border-line bg-subtle/60 px-4 py-2.5 text-sm text-mute">
                Next disbursement gated by: <span className="font-medium text-ink">Structural frame</span> evidence.
              </div>
              <EvidenceByProject projectId={projectId} />
            </div>
          )}
          {tab === "audit" && (
            <div className="space-y-6">
              {profile.group === "oversight" && <PendingEditRequestPanel projectId={projectId} />}
              <AuditTrail events={projectEvents} />
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink">Governance history</h3>
                <ProjectGovernanceHistory projectId={projectId} />
              </div>
            </div>
          )}
          {tab === "team" && <TeamTab projectId={projectId} />}
          {tab === "analytics" && <AnalyticsSurface scopeLabel={project.name} />}
        </div>
      </div>
      <RecordSpendDrawer projectId={projectId} open={recordOpen} onOpenChange={setRecordOpen} />
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} projectId={project?.id ?? projectId} projectName={project?.name} />
      {showAddSubProject && (
        <CreateProjectModal
          open={addSubProjectOpen}
          onOpenChange={setAddSubProjectOpen}
          presetParentProjectId={project.id}
          programmeId={programmeId}
        />
      )}
      {profile.group === "oversight" && (
        <>
          <ChangeProjectStatusModal
            open={changeStatusOpen}
            onOpenChange={setChangeStatusOpen}
            projectId={project.id}
            currentStatus={project.status}
          />
          <EditProjectModal
            open={editProjectOpen}
            onOpenChange={setEditProjectOpen}
            projectId={project.id}
            initialValues={{
              name: project.name,
              description: project.description,
              region: project.region,
              district: project.district,
              budgeted: project.budgeted,
            }}
          />
        </>
      )}
      {profile.group === "contractor" && !demoModeActive() && (
        <RequestProjectEditModal
          open={requestEditOpen}
          onOpenChange={setRequestEditOpen}
          projectId={project.id}
          currentValues={{
            name: project.name,
            description: project.description,
            region: project.region,
            district: project.district,
            budgeted: project.budgeted,
          }}
        />
      )}
    </WorkspaceShell>
  );
}

/** First letter of up to two words, e.g. "Grace Nansubuga" -> "GN". */
function initialsOf(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";
}

function TeamTab({ projectId }: { projectId: string }) {
  const seed = useSeed();
  const profile = useActiveProfile();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [assignLeadOpen, setAssignLeadOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const project = seed.projects.find((p) => p.id === projectId);
  const { organisations: orgs, isLoading: orgsLoading } = useProjectOrganisations(projectId);
  const { members: people, isLoading: peopleLoading } = useProjectMembers(projectId);
  // Add/Invite are appoint actions - only groups with appoint authority in
  // the cascade role model should see them. A funder reaching this tab
  // (via the new Team link on their insights dashboard) gets the
  // read-only tables below, nothing else.
  const canManageMembers = profile.group === "oversight" || profile.group === "contractor";

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-ink">Organisations</h3>
          <div className="flex flex-wrap items-center gap-2">
            {profile.group === "oversight" && (
              <Button size="sm" variant="secondary" onClick={() => setAssignLeadOpen(true)}><Building2 className="h-4 w-4" /> Assign lead contractor</Button>
            )}
            {canManageMembers && (
              <>
                <Button size="sm" variant="secondary" onClick={() => setAddMembersOpen(true)}><Users className="h-4 w-4" /> Add members</Button>
                <Button size="sm" variant="secondary" onClick={() => setInviteOpen(true)}><UserPlus className="h-4 w-4" /> Invite</Button>
              </>
            )}
          </div>
        </div>
        <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} projectId={project?.id ?? projectId} projectName={project?.name} />
        <AddMembersModal open={addMembersOpen} onOpenChange={setAddMembersOpen} projectId={project?.id ?? projectId} projectName={project?.name} />
        <AssignLeadContractorModal open={assignLeadOpen} onOpenChange={setAssignLeadOpen} projectId={project?.id ?? projectId} projectName={project?.name} />
        <RemoveMemberModal
          open={!!removeTarget}
          onOpenChange={(o) => !o && setRemoveTarget(null)}
          projectId={projectId}
          membershipId={removeTarget?.membershipId ?? null}
          memberName={removeTarget?.name ?? ""}
        />
        {orgsLoading ? (
          <p className="text-sm text-faint">Loading organisations…</p>
        ) : orgs.length === 0 ? (
          <p className="rounded-md border border-line bg-subtle px-3 py-3 text-sm text-mute">
            No lead contractor assigned yet.
          </p>
        ) : (
          <DataTable
            columns={[
              { key: "name", header: "Organisation", cell: (o) => <span className="font-medium text-ink">{o.name}</span> },
              { key: "tier", header: "Tier", cell: (o) => <Pill variant={o.tier === "prime" ? "brand" : "neutral"}>{o.tier === "prime" ? "Prime" : "Sub"}</Pill> },
              { key: "kyc", header: "KYC", cell: (o) => <Pill variant={["approved", "verified"].includes(o.kycState) ? "success" : "warn"}>{o.kycState.replace("_", " ")}</Pill> },
              { key: "status", header: "Status", cell: (o) => <Pill variant={o.status === "active" ? "success" : "danger"}>{o.status}</Pill> },
            ]}
            rows={orgs}
            rowKey={(o) => o.id}
          />
        )}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-semibold text-ink">People</h3>
        {peopleLoading ? (
          <p className="text-sm text-faint">Loading members…</p>
        ) : people.length === 0 ? (
          <p className="rounded-md border border-line bg-subtle px-3 py-3 text-sm text-mute">
            No one has been added to this project yet.
          </p>
        ) : (
          <DataTable
            columns={[
              {
                key: "name",
                header: "Name",
                cell: (p) => (
                  <div className="flex items-center gap-2">
                    <Avatar initials={initialsOf(p.name)} size={24} />
                    <span className="font-medium text-ink">{p.name}</span>
                  </div>
                ),
              },
              { key: "role", header: "Role", cell: (p) => <span className="text-mute">{roleLabelFor(p.role)}</span> },
              { key: "organisation", header: "Organisation", cell: (p) => <span className="text-mute">{p.organisationName ?? "—"}</span> },
              {
                key: "status",
                header: "Status",
                cell: (p) => (
                  <Pill variant={p.status === "active" ? "success" : p.status === "pending" ? "warn" : "danger"}>
                    {p.status}
                  </Pill>
                ),
              },
              ...(profile.group === "oversight"
                ? [
                    {
                      key: "actions",
                      header: "",
                      cell: (p: TeamMember) =>
                        p.membershipId != null && p.status !== "revoked" ? (
                          <Button size="sm" variant="ghost" onClick={() => setRemoveTarget(p)}>
                            <UserMinus className="h-4 w-4" /> Remove
                          </Button>
                        ) : null,
                    },
                  ]
                : []),
            ]}
            rows={people}
            rowKey={(p) => p.id}
          />
        )}
      </div>
    </div>
  );
}
